import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, viewChild, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CheckboxModule } from 'primeng/checkbox';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { OnboardingService } from '../../services/onboarding.service';
import { ProgressService } from '../../services/progress.service';
import { Web } from 'src/app/shared/constants/AppConstats';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';

@Component({
  selector: 'app-review-and-submit',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, CheckboxModule, RecaptchaModule, ButtonComponent],
  templateUrl: './review-and-submit.component.html',
  styleUrls: ['./review-and-submit.component.scss'],
})
export class ReviewAndSubmitComponent implements OnInit, OnDestroy {
  readonly captchaRef = viewChild.required<RecaptchaComponent>('captchaRef');

  protected readonly onboardingService = inject(OnboardingService);
  protected readonly progressService = inject(ProgressService);
  protected readonly router = inject(Router);
  protected readonly toastr = inject(ToastrService);
  protected readonly destroy$ = new Subject<void>();

  protected accountInformation: any;
  protected adminInformation: any;
  protected clubInformation: any;
  protected submitting = signal(false);
  protected progressMessage = signal('Creating your club account...');
  private progressInterval: any;

  private readonly progressMessages = [
    'Creating your club account...',
    'Setting up your venue...',
    'Configuring activities...',
    'Preparing email templates...',
    'Almost there...',
  ];

  ngOnInit(): void {
    const info = this.onboardingService.getAccountInformation();
    this.accountInformation = info;
    this.adminInformation = info.adminInformation;
    this.clubInformation = info.clubInformation;

    if (this.adminInformation.email) {
      this.adminInformation.email = this.adminInformation.email.replace(/^mailto:/i, '');
    }
    if (this.clubInformation.coachDetails.email) {
      this.clubInformation.coachDetails.email = this.clubInformation.coachDetails.email.replace(/^mailto:/i, '');
    }
  }

  ngOnDestroy(): void {
    this.stopProgressMessages();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startProgressMessages(): void {
    let index = 0;
    this.progressMessage.set(this.progressMessages[0]);
    this.progressInterval = setInterval(() => {
      index = (index + 1) % this.progressMessages.length;
      this.progressMessage.set(this.progressMessages[index]);
    }, 2000);
  }

  private stopProgressMessages(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  protected onSubmit(): void {
    const missing: string[] = [];
    if (!this.adminInformation.clubName) missing.push('Club Name');
    if (!this.adminInformation.contactPersonName) missing.push('Contact Person');
    if (!this.adminInformation.email) missing.push('Admin Email');
    if (!this.adminInformation.phoneNumber) missing.push('Phone Number');
    if (!this.adminInformation.password) missing.push('Password');
    if (!this.clubInformation.clubAddress.address) missing.push('Address');
    if (!this.clubInformation.clubAddress.state) missing.push('State/County');
    if (!this.clubInformation.clubAddress.postCode) missing.push('Postcode');
    if (!this.clubInformation.clubAddress.country) missing.push('Country');
    if (!this.clubInformation.currency) missing.push('Currency');
    if (!this.clubInformation.clubTnC) missing.push('T&C URL');
    if (!this.clubInformation.coachDetails.firstName) missing.push('Coach First Name');
    if (!this.clubInformation.coachDetails.lastName) missing.push('Coach Last Name');
    if (!this.clubInformation.coachDetails.email) missing.push('Coach Email');
    if (!this.clubInformation.coachDetails.gender) missing.push('Coach Gender');

    if (missing.length > 0) { this.toastr.error(`Missing details: ${missing.join(', ')}`); return; }
    if (!this.clubInformation.termsAccepted) { this.toastr.error('You must accept the Terms and Conditions to continue'); return; }

    this.captchaRef().execute();
  }

  protected onCaptchaResolved(token: string | null): void {
    if (!token) return;
    this.submitting.set(true);
    this.startProgressMessages();

    const payload = {
      parentclubId: '', clubId: '',
      activityId: this.adminInformation.primaryActivityId,
      memberId: '', action_type: 0,
      device_type: Web.DeviceType, app_type: Web.AppType, device_id: '',
      updated_by: 'super_admin', created_by: 'super_admin',
      parent_club_name: this.adminInformation.clubName,
      admin_email: this.adminInformation.email,
      admin_password: this.adminInformation.password,
      website: '',
      terms_url: this.clubInformation.clubTnC,
      app_icon_url: this.accountInformation.appIconUrl || '',
      contact_name: this.adminInformation.contactPersonName,
      contact_phone: String(this.adminInformation.phoneNumber),
      corporate_address_firstline: this.clubInformation.clubAddress.address,
      corporate_address_secondline: this.clubInformation.clubAddress.address2,
      corporate_address_city: this.clubInformation.clubAddress.city,
      corporate_address_state: this.clubInformation.clubAddress.state,
      currency_code: this.clubInformation.currency,
      country_name: this.clubInformation.clubAddress.countryName || this.clubInformation.clubAddress.country,
      country: this.clubInformation.clubAddress.country,
      postcode: this.clubInformation.clubAddress.postCode,
      is_ap_play_enabled: true, is_superuser_parentclub: 1, playground_switch: 0,
      camp_notify_unenroll: true, android_version_switch: true, android_version: 0,
      android_force_update: true, android_version_msg: 'There is a new version of the app available',
      android_app_link: '', ios_version_switch: true, ios_version: 0,
      ios_force_update: true, ios_version_msg: 'There is a new version of the app available',
      ios_app_link: '', facebook_url: '', instagram_url: '', twitter_url: '', linkedin_url: '',
      primaryColor: '#dc2626', secondaryColor: '#ffffff', short_name: this.deriveShortName(this.adminInformation.clubName),
      coach_first_name: this.clubInformation.coachDetails.firstName,
      coach_last_name: this.clubInformation.coachDetails.lastName,
      coach_email: this.clubInformation.coachDetails.email,
      coach_gender: this.clubInformation.coachDetails.gender,
      coach_password: this.generatePassword()
    };

    this.onboardingService.createParentClub(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.stopProgressMessages();
          sessionStorage.setItem('clubName', this.adminInformation.clubName);
          sessionStorage.setItem('onboarding_admin_email', this.adminInformation.email);
          sessionStorage.setItem('onboarding_coach_email', this.clubInformation.coachDetails.email);
          sessionStorage.setItem('onboarding_coach_name', `${this.clubInformation.coachDetails.firstName} ${this.clubInformation.coachDetails.lastName}`);
          this.onboardingService.resetAccountInformation();
          this.progressService.completedStep2();
          this.progressService.accountCreated();
          this.toastr.success('Welcome onboard! Your Club App is ready.');
          this.router.navigate(['/auth/onboarding/welcome']);
        },
        error: (error) => {
          this.stopProgressMessages();
          this.submitting.set(false);
          this.captchaRef().reset();
          const msg = error.error?.message || error.message || '';
          if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('unique constraint') || msg.toLowerCase().includes('already exists')) {
            this.toastr.error('This admin email is already registered. Please use a different email address.');
          } else {
            this.toastr.error(msg || 'Failed to create account. Please try again');
          }
        }
      });
  }

  protected onCaptchaError(error: any): void {
    this.toastr.error('reCAPTCHA Timeout - Please check your network or domain configuration');
    this.captchaRef().reset();
  }

  protected goBack(): void {
    this.router.navigate(['/auth/onboarding/club-details']);
  }

  private generatePassword(): string {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  private deriveShortName(clubName: string): string {
    const trimmed = (clubName || '').trim();
    if (!trimmed) { return ''; }
    return trimmed.length <= 20 ? trimmed : trimmed.slice(0, 20).trim();
  }
}
